import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  clearFormLanguageRestriction,
  setFormLanguageRestriction
} from 'app/redux/actions/FormRestrictionsActions'
import { setUserData } from 'app/redux/actions/UserActions'
import { ReduxUser } from 'app/redux/ReduxInterfaces'
import { isNil } from 'lodash'

interface UseFormLanguageRestrictionResult {
  /** Whether the unsupported-language warning dialog should be shown */
  showLangWarning: boolean
  /** Call when the user confirms the warning — switches the page language to the first supported language */
  switchToSupportedLanguage: () => void
}

/**
 * Handles language restriction logic for forms tied to a funding stream.
 *
 * When the user's language is not in `avaliableInLanguages`:
 * - Shows a warning dialog (`showLangWarning`)
 * - On confirm, temporarily switches `user.language` in Redux to the first
 *   supported language (without saving to Salesforce), so Lingui re-renders
 *   the entire page in that language
 * - Restores the original language when the form unmounts
 *
 * Also keeps `allowedLanguages` in Redux in sync so the topbar language
 * dropdown can disable unsupported options while the form is open.
 */
export function useFormLanguageRestriction (
  avaliableInLanguages: string[] | undefined
): UseFormLanguageRestrictionResult {
  const user = useSelector((state: { user: ReduxUser }) => state.user)
  const dispatch = useDispatch()
  const [showLangWarning, setShowLangWarning] = useState(false)
  // Stores the user's original language so it can be restored on unmount
  const originalLanguageRef = useRef<string | null>(null)

  const userRef = useRef(user)

  useEffect(() => { userRef.current = user })

  useEffect(() => {
    if (!isNil(avaliableInLanguages) && avaliableInLanguages.length > 0) {
      dispatch(setFormLanguageRestriction(avaliableInLanguages))
      if (!avaliableInLanguages.includes(user.language)) {
        setShowLangWarning(true)
      }
    }
    return () => {
      dispatch(clearFormLanguageRestriction())
      if (!isNil(originalLanguageRef.current)) {
        dispatch(setUserData({ ...userRef.current, language: originalLanguageRef.current }))
        originalLanguageRef.current = null
      }
    }
  }, [avaliableInLanguages])

  const switchToSupportedLanguage = (): void => {
    if (isNil(avaliableInLanguages) || avaliableInLanguages.length === 0) return
    setShowLangWarning(false)
    if (isNil(originalLanguageRef.current)) {
      originalLanguageRef.current = user.language
    }
    dispatch(setUserData({ ...userRef.current, language: avaliableInLanguages[0] }))
  }

  return { showLangWarning, switchToSupportedLanguage }
}
